import api from "@/lib/axios";
import { BusinessCategory } from "@/types/user";

export type CancellationReason =
  | "too_expensive"
  | "not_using"
  | "better_alternative"
  | "missing_features"
  | "other";

export interface CancelSubscriptionPayload {
  reason: CancellationReason;
  otherText?: string;
}

// STA-743 — founder-initiated cancellation. After success, the caller should
// invalidate the ["me"] query so DangerZoneSection re-renders in cancelled state.
export const cancelOwnSubscription = async (
  payload: CancelSubscriptionPayload,
): Promise<{ subscriptionEndsAt: string; stripeCancelOk: boolean }> => {
  const { data } = await api.post("/licensees/cancel-subscription", payload);
  return data;
};

export enum FoundersKeys {
  Earnings = "founder-earnings",
  AllowedPaymentMethods = "founder-allowed-payment-methods",
  ReferralCode = "founder-referral-code",
  GET_FOUNDERS = "get-founders",
  GET_FOUNDER = "get-founder",
  SET_COHORT = "founder-set-cohort",
  CHECKOUT_LINK = "founder-checkout-link",
  GENERATE_CERTIFICATE = "founder-generate-certificate",
}

interface EarningsResponse {
  currentMonth: number;
  lastMonth: number;
  ytd: number;
  monthlyBreakdown: Array<{
    month: string;
    year: string;
    timestamp: number;
    earnings: number;
  }>;
}

export const getFounderEarnings = async (): Promise<EarningsResponse> => {
  const response = await api.get(`/payments/earnings`);
  return response.data;
};

// Function to get or generate referral code
export const getFounderReferralCode = async (): Promise<{
  referralCode: string;
}> => {
  const response = await api.get(`/licensees/referral-code`);
  return response.data;
};

export const setOwnCohort = async (cohortId: string) => {
  const response = await api.patch(`/licensees/cohort`, { cohortId });
  return response.data;
};

export const getFounderCheckoutLink = async (
  usePaymentPlan = false,
): Promise<{ url: string }> => {
  const response = await api.get(`/licensees/checkout-link`, {
    params: { paymentPlan: usePaymentPlan ? "true" : undefined },
  });
  return response.data as { url: string };
};

// Admin API calls for managing licensees (using the correct api import)
export interface Founder {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  country: string;
  mentor: any;
  cohort: any;
  subscriptionActive: boolean;
  stripeActive: boolean;
}

export const createFounder = async (payload: Partial<Founder>) => {
  const response = await api.post("/licensees", payload);
  return response.data as { user: Founder };
};

export const getFounders = async () => {
  const response = await api.get("/licensees");
  return response.data as Founder[];
};

export const getFounder = async (id: string) => {
  const response = await api.get(`/licensees/${id}`);
  return response.data as Founder;
};

export const updateFounder = async (
  id: string,
  updatedFounder: Partial<Founder>,
) => {
  const response = await api.patch(`/licensees/${id}`, updatedFounder);
  return response.data as Founder;
};

export const sendFounderStripeReminder = async (id: string) => {
  const response = await api.post(
    `/licensees/${id}/send-stripe-onboarding-email`,
  );
  return response.data;
};

// Function to get current founder profile
export const getOwnFounder = async () => {
  const response = await api.get("/licensees/me");
  return response.data as Founder;
};

// Function to update founder profile settings
export interface UpdateFounderSettingsPayload {
  niche?: string;
  nicheWho?: string;
  nicheWhat?: string;
  businessCategory?: BusinessCategory[]; // Will be transformed to lowercase before sending
}

export const updateOwnFounderSettings = async (
  payload: UpdateFounderSettingsPayload,
) => {
  // Transform businessCategory to lowercase for backend
  const transformedPayload = {
    ...payload,
    businessCategory: payload.businessCategory?.map((cat) => cat.toLowerCase()),
  };

  const response = await api.patch("/licensees/profile", transformedPayload);
  return response.data;
};

// Function to generate founder certificate
export const generateFounderCertificate = async (): Promise<{
  certificateUrl: string;
}> => {
  const response = await api.post("/levels/certificate");
  return response.data;
};

// Function to mark agents library announcement as seen
export const markAgentsLibraryAnnouncementSeen = async (): Promise<void> => {
  await api.post("/licensees/mark-agents-library-announcement-seen");
};
